import { InstanceStatus } from "../types";

// Time remaining interface
export interface TimeRemaining {
  hours: number;
  minutes: number;
  seconds: number;
  isExpired: boolean;
  totalSeconds: number;
}

/**
 * Parse a date string safely across all browsers (including Safari/iOS).
 * Safari cannot parse ISO strings with timezone offsets like "+02:00".
 */
function safeParseDate(date: string | null | undefined): Date | null {
  if (!date) return null;
  const d = new Date(date);
  if (!isNaN(d.getTime())) return d;
  // Fallback: replace timezone offset format for Safari compatibility
  const fixed = date.replace(/([+-]\d{2}):(\d{2})$/, "$1$2");
  const d2 = new Date(fixed);
  return isNaN(d2.getTime()) ? null : d2;
}

// Date formatting
export function formatDate(date: string | null | undefined): string {
  const d = safeParseDate(date);
  if (!d) return "-";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function formatDateTime(date: string | null | undefined): string {
  const d = safeParseDate(date);
  if (!d) return "-";
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

export function formatRelativeTime(date: string): string {
  const now = new Date();
  const then = new Date(date);
  const diffMs = now.getTime() - then.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 0) return `${diffDays}d ago`;
  if (diffHours > 0) return `${diffHours}h ago`;
  if (diffMins > 0) return `${diffMins}m ago`;
  return "just now";
}

// Currency formatting
export function formatCurrency(cents: number, currency = "USD"): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

// Duration formatting
export function formatDuration(hours: number): string {
  if (hours < 24) {
    return `${hours} hour${hours !== 1 ? "s" : ""}`;
  }
  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  if (remainingHours === 0) {
    return `${days} day${days !== 1 ? "s" : ""}`;
  }
  return `${days}d ${remainingHours}h`;
}

export function formatTimeRemaining(time: TimeRemaining): string {
  if (time.isExpired) return "Expired";

  const parts: string[] = [];
  if (time.hours > 0) parts.push(`${time.hours}h`);
  if (time.minutes > 0 || time.hours > 0) parts.push(`${time.minutes}m`);
  parts.push(`${time.seconds}s`);

  return parts.join(" ");
}

export function calculateTimeRemaining(expiresAt: string): TimeRemaining {
  const now = new Date().getTime();
  const expiry = new Date(expiresAt).getTime();
  const difference = expiry - now;

  if (difference <= 0) {
    return {
      hours: 0,
      minutes: 0,
      seconds: 0,
      isExpired: true,
      totalSeconds: 0,
    };
  }

  const totalSeconds = Math.floor(difference / 1000);
  const hours = Math.floor(difference / (1000 * 60 * 60));
  const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((difference % (1000 * 60)) / 1000);

  return { hours, minutes, seconds, isExpired: false, totalSeconds };
}

// Instance helpers
export function isInstanceActive(status: InstanceStatus): boolean {
  return (
    status === "RUNNING" || status === "PROVISIONING" || status === "PENDING"
  );
}

export function getStatusColor(
  status: InstanceStatus,
): "success" | "warning" | "error" | "info" | "default" {
  switch (status) {
    case "RUNNING":
      return "success";
    case "PROVISIONING":
    case "PENDING":
      return "warning";
    case "FAILED":
      return "error";
    case "EXPIRED":
    case "TERMINATED":
    default:
      return "default";
  }
}
